import React from 'react';
import { withRouter } from 'react-router-dom';
import { Input, Row, Col, message /*Tooltip*/ } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  WarningOutlined,
  /*InfoCircleOutlined,*/
} from '@ant-design/icons';
import './SearchForm.less';
import ReasonModal from './reasonModal/ReasonModal';
import { AdvancedSearchForm /*UploadForm*/ } from './';
import { UnpaidFeeSingleResultForm } from '../bulletin/unpaidfee';
import { OPSLAG_ERROR, OPSLAG_SUCCESS } from './../../actions/Search';
import { textsDK } from './../../utility/LanguageUtils';

const Search = Input.Search;

class SearchForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      advancedSearch: false,
      personNotFoundMessage: 'Personen blev ikke fundet.',
      searchOptionText: 'Søg på navn',
      showSingleResult: false,
      singleResultStyle: '',
      unpaidFee: null,
      modalVisible: false,
    };
  }

  /**
   * Action success & errors handling
   * @param prevProps - previous props from earlier state
   */
  componentDidUpdate(prevProps) {
    this.handleActionResponseNotifications(this.props.actionResponse);
  }

  /**
   * Handles notifications for the action response
   * @param actionResponse - the response of the action made
   */
  handleActionResponseNotifications(actionResponse) {
    if (actionResponse === null) {
      return;
    }

    switch (actionResponse.get('type')) {
      case OPSLAG_SUCCESS: {
        this.handleOpslagSuccess(this.props.unpaidFees);
        break;
      }

      case OPSLAG_ERROR: {
        const errorCode = actionResponse.getIn(['status', 'code']);
        if (errorCode === 417) {
          this.setState({ modalVisible: true });
        } else {
          message.error('Der er opstået en fejl, prøv igen.');
        }
        break;
      }
      default:
        break;
    }
    this.props.clearActionResponse();
  }

  hideModal = () => {
    this.setState({
      modalVisible: false,
    });
  };

  handleOpslagSuccess(unpaidFees) {
    if (this.state.prevInput) {
      this.setState({ prevInput: null });
    }
    switch (unpaidFees.size) {
      case 0:
        message.error(this.props.error.message);
        return;

      case 1:
        if (unpaidFees.get(0).get('Value') === null) {
          message.error(textsDK[unpaidFees.get(0).get('Text')]);
          return;
        } else {
          this.setState({ showSingleResult: true });
        }
        return;

      default:
        this.props.history.push('/bulletin');
        return;
    }
  }

  /**
   * Sends an advanced search query to backend.
   * @param input the query to the backend.
   */
  handleAdvancedSearch = (input) => {
    this.setState({ showSingleResult: false, prevInput: input });
    this.props.opslag(input);
  };

  /**
   * Sends an simple search query to backend.
   * @param input the query to the backend.
   */
  handleSearch = (input) => {
    this.setState({ showSingleResult: false });
    const inputArray = input.split(',');
    let inputObjs = [];
    inputArray.forEach((inputValue) => inputObjs.push({ administrativeId: inputValue.trim() }));
    this.setState({ prevInput: inputObjs });
    this.props.opslag(inputObjs);
  };

  /**
   * Sends an advanced search query to backend based on results from file that was uploaded.
   * @param input the query to the backend.
   */
  handleFileSearch = (input) => {
    this.setState({ showSingleResult: false });
    const splitRegex = /[\r\n]+/;
    const inputArray = input.split(splitRegex);
    let inputObjs = [];
    inputArray.forEach((inputLine) => inputObjs.push(this.handleSplitCsvLine(inputLine)));
    this.props.opslag(inputObjs);
  };

  /**
   * Creates an object based on a line from a csv file.
   * @param inputLine is the line from the csv file.
   * @returns {*} the object that is to be inserted in to a query.
   */
  handleSplitCsvLine(inputLine) {
    const lineArray = inputLine.split(';');
    if (lineArray.length !== 11) {
      return null;
    }

    const administrativeId = lineArray.slice(0, 3).find((value) => value !== '');

    return {
      administrativeId,
      firstName: lineArray[3],
      lastName: lineArray[4],
      streetName: lineArray[5],
      streetNumber: lineArray[6],
      streetHouseLetter: lineArray[7],
      streetFloor: lineArray[8],
      zipCode: lineArray[9],
      city: lineArray[10],
    };
  }

  /**
   * Sets view mode between simple and advanced search.
   */
  handleSetAdvancedSearch = () => {
    const advancedSearch = !this.state.advancedSearch;
    this.setState({
      showSingleResult: false,
      advancedSearch: advancedSearch,
    });
    this.setState({ searchOptionText: advancedSearch ? 'Søg på nummer' : 'Søg på navn' });
  };

  /**
   * Gets the single result.
   * @returns {*}
   */
  handleGetSingleResult = () => {
    return this.props.unpaidFees.get(0);
  };

  /**
   * Determines weather the result is accepted, rejected or is a warning.
   * @param value is the value code of the result
   * @returns {string} a string matching part of a css style.
   */
  handleGetCardTitleStyle = (value) => {
    switch (value) {
      case 1:
      case 3:
        return 'accepted';
      case 2:
        return 'rejected';
      default:
        return 'warning';
    }
  };

  /**
   * Removes the single result from the view.
   */
  handleRemoveResultCard = () => {
    this.setState({ showSingleResult: false });
  };

  /**
   * Creates the correct layout based on the search parameters.
   * @param unpaidFee is the result of the search
   * @returns {*} the body layout based on the result.
   */
  getSearchedValue(unpaidFee) {
    let text = '';
    let icon = '';
    if (unpaidFee.get('AKRnumber') != null) {
      text = 'AKR Nr.: ' + unpaidFee.get('AKRnumber');
    } else if (unpaidFee.get('CPRnumber') != null) {
      text = 'CPR Nr.: ' + unpaidFee.get('CPRnumber');
    } else if (unpaidFee.get('CVRnumber') != null) {
      text = 'CVR Nr.: ' + unpaidFee.get('CVRnumber');
    } else if (unpaidFee.get('Firstname') != null && unpaidFee.get('Lastname') != null) {
      text = 'Navn: ' + unpaidFee.get('Firstname') + ' ' + unpaidFee.get('Lastname');
    } else if (unpaidFee.get('Lastname') != null) {
      text = 'Virksomhedsnavn: ' + unpaidFee.get('Lastname');
    } else {
      text = 'ERROR';
    }

    const colorCode = this.handleGetCardTitleStyle(unpaidFee.get('Value'));

    switch (colorCode.toLowerCase()) {
      case 'accepted':
        icon = <CheckOutlined />;
        break;

      case 'rejected':
        icon = <CloseOutlined />;
        break;

      default:
        icon = <WarningOutlined />;
        break;
    }

    return (
      <Row type={'flex'} align={'middle'}>
        <Col span={2}>{icon}</Col>
        <Col span={22}>
          <label>{text}</label>
        </Col>
      </Row>
    );
  }

  /**
   * Gets tool tip information/layout about csv upload.
   * @returns {*} returns body for tool tip.
   */
  getUploadTooltip() {
    return (
      <Row className={'tooltip-small-text'}>
        <Col>
          <div>
            - Det er kun tilladt at importere .csv filer <br />
            - Fil formatet består af 11 kolonner: <br />
            1. AKRNummer <br />
            2. CPRNummer <br />
            3. CVRNummer <br />
            4. Fornavn <br />
            5. Efternavn <br />
            6. Vejnavn <br />
            7. Husnummer <br />
            8. Husbogstav <br />
            9. Etage <br />
            10. Postnummer <br />
            11. By <br />
            - Alle værdier skal adskilles af et ';' <br />
            (Dette vil automatisk ske opretter man filen gennem Excel)
          </div>
        </Col>
      </Row>
    );
  }

  render() {
    return (
      <Row className={'search-form'}>
        <Col span={24}>
          <div className={'search-label'}>
            <label>Ubetalt gebyr søgning</label>
          </div>
          {this.state.advancedSearch ? (
            <AdvancedSearchForm
              opslag={this.handleAdvancedSearch}
              dawaLookup={this.props.dawaLookup}
              addressSuggestions={this.props.addressSuggestions}
            />
          ) : (
            <Search
              placeholder={'Søg på CPR/CVR/AKR'}
              onSearch={(value) => this.handleSearch(value)}
              // defaultValue={ENV_TEST_UNPAIDFEE}
              enterButton
            />
          )}
          <Row type={'flex'} className={'spacing-top'}>
            {/*See DFIM-90 to see why this is commented out
            <Col span={12} align={'left'}>
               <div className={'div-inline'}>
                <UploadForm className={'upload-list-inline'} handleSearch={this.handleFileSearch} />
              </div> 
              <div className={'div-tooltip'}>
                <Tooltip
                  title={this.getUploadTooltip()}
                  placement='rightTop'
                  autoAdjustOverflow={true}
                  mouseEnterDelay={0.1}
                >
                  <InfoCircleOutlined className={'icon-info'} />
                </Tooltip>
              </div>
            </Col>
            */}
            <Col span={12} align={'right'} className={'advanced-search'}>
              <label className={'label-link'} onClick={this.handleSetAdvancedSearch}>
                {this.state.searchOptionText}
              </label>
            </Col>
          </Row>
          <Row className={'spacing-top'}>
            <Col span={24}>
              <div>
                {this.state.showSingleResult ? (
                  <UnpaidFeeSingleResultForm
                    colorCode={this.handleGetCardTitleStyle(
                      this.props.unpaidFees.get(0).get('Value')
                    )}
                    cardTitle={this.getSearchedValue(this.props.unpaidFees.get(0))}
                    unpaidFee={this.handleGetSingleResult()}
                    removeResultCard={this.handleRemoveResultCard}
                  />
                ) : null}
              </div>
            </Col>
          </Row>
          <ReasonModal
            visible={this.state.modalVisible}
            hideModal={this.hideModal}
            loading={this.props.isLoading}
            opslag={this.props.opslag}
            prevInput={this.state.prevInput}
          />
        </Col>
      </Row>
    );
  }
}

export default withRouter(SearchForm);
